import { useEffect, useMemo, useState } from "react";
import { useBrandPageContext } from "./BrandLayout";
import {
  useContextDocs,
  useCreateContextDoc,
  useDeleteContextDoc,
  useUpdateContextDoc,
} from "../hooks/useContextDocs";
import { LoadErrorState, LoadingState } from "../components/ui/LoadState";
import { CONTEXT_DOC_TEMPLATES, type ContextDocTemplate } from "../lib/context-doc-templates";
import type { BrandContextDoc, BrandContextDocApplyWhen, BrandContextDocType } from "../../../shared/types.ts";

type DraftDoc = {
  slug: string;
  name: string;
  doc_type: BrandContextDocType;
  surface: string;
  apply_when: BrandContextDocApplyWhen[];
  usage_prompt: string;
  content: string;
  enabled: boolean;
  sort_order: number;
};

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

function draftFromTemplate(template: ContextDocTemplate): DraftDoc {
  return {
    slug: slugify(template.name),
    name: template.name,
    doc_type: template.doc_type,
    surface: template.surface ?? "",
    apply_when: template.apply_when,
    usage_prompt: template.usage_prompt ?? "",
    content: template.content,
    enabled: true,
    sort_order: 10,
  };
}

function draftFromDoc(doc: BrandContextDoc): DraftDoc {
  return {
    slug: doc.slug,
    name: doc.name,
    doc_type: doc.doc_type,
    surface: doc.surface ?? "",
    apply_when: doc.apply_when,
    usage_prompt: doc.usage_prompt ?? "",
    content: doc.content,
    enabled: doc.enabled,
    sort_order: doc.sort_order,
  };
}

function emptyDraft(): DraftDoc {
  return {
    slug: "",
    name: "",
    doc_type: "persona",
    surface: "",
    apply_when: ["all"],
    usage_prompt: "",
    content: "",
    enabled: true,
    sort_order: 10,
  };
}

function applyWhenLabel(value: BrandContextDocApplyWhen): string {
  if (value === "all") return "Writing + reviewing";
  return value === "writing" ? "Writing" : "Reviewing";
}

export function BrandContextDocs() {
  const { brand, brandId } = useBrandPageContext();
  const docsQuery = useContextDocs(brandId, { includeDisabled: true });
  const createDoc = useCreateContextDoc(brandId);
  const updateDoc = useUpdateContextDoc(brandId);
  const deleteDoc = useDeleteContextDoc(brandId);
  const docs = docsQuery.data?.data ?? [];
  const [editorMode, setEditorMode] = useState<"edit" | "create">("edit");
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftDoc>(() => emptyDraft());
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEditorMode("edit");
    setSelectedSlug(null);
    setDraft(emptyDraft());
    setNotice(null);
    setError(null);
  }, [brandId]);

  // A failed list is not an empty list: never switch to create mode on error,
  // or the operator would author a duplicate of a doc that already exists.
  const docsFailed = docsQuery.isError && !docsQuery.data;

  useEffect(() => {
    if (docsQuery.isLoading || docsFailed) {
      return;
    }
    if (!docs.length) {
      if (selectedSlug !== null || editorMode !== "create") {
        setEditorMode("create");
        setSelectedSlug(null);
        setDraft(emptyDraft());
        return;
      }
      return;
    }
    if (selectedSlug && docs.some((doc) => doc.slug === selectedSlug)) {
      return;
    }
    if (editorMode === "create") {
      return;
    }
    const firstDoc = docs[0];
    setSelectedSlug(firstDoc?.slug ?? null);
    if (firstDoc) setDraft(draftFromDoc(firstDoc));
  }, [docs, docsQuery.isLoading, docsFailed, editorMode, selectedSlug]);

  const selectedDoc = useMemo(
    () => (selectedSlug ? docs.find((doc) => doc.slug === selectedSlug) ?? null : null),
    [docs, selectedSlug],
  );

  const saving = createDoc.isPending || updateDoc.isPending || deleteDoc.isPending;

  async function onSave() {
    setError(null);
    setNotice(null);
    if (!draft.name.trim()) {
      setError("Name is required before saving this context doc.");
      return;
    }
    try {
      const payload = {
        name: draft.name.trim(),
        slug: draft.slug.trim(),
        doc_type: draft.doc_type,
        surface: draft.surface.trim() || null,
        apply_when: draft.apply_when,
        usage_prompt: draft.usage_prompt.trim() || undefined,
        content: draft.content,
        enabled: draft.enabled,
        sort_order: draft.sort_order,
      };
      if (selectedDoc) {
        const result = await updateDoc.mutateAsync({ ...payload, slug: selectedDoc.slug });
        setEditorMode("edit");
        setSelectedSlug(result.data.slug);
        setDraft(draftFromDoc(result.data));
        setNotice(result.data.enabled ? "Context doc updated." : "Context doc archived in place.");
      } else {
        const result = await createDoc.mutateAsync(payload);
        setEditorMode("edit");
        setSelectedSlug(result.data.slug);
        setDraft(draftFromDoc(result.data));
        setNotice("Context doc created.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save this context doc.");
    }
  }

  async function onArchive() {
    if (!selectedDoc) return;
    setError(null);
    setNotice(null);
    try {
      const result = await deleteDoc.mutateAsync(selectedDoc.slug);
      setDraft(draftFromDoc(result.data));
      setNotice("Context doc archived. It stays visible here for audit or restore.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not archive this context doc.");
    }
  }

  function toggleApplyWhen(value: BrandContextDocApplyWhen) {
    setDraft((current) => {
      if (value === "all") {
        return { ...current, apply_when: current.apply_when.includes("all") ? ["writing"] : ["all"] };
      }
      const next = current.apply_when.filter((entry) => entry !== "all");
      const hasValue = next.includes(value);
      const updated = hasValue ? next.filter((entry) => entry !== value) : [...next, value];
      return { ...current, apply_when: updated.length ? updated : ["all"] };
    });
  }

  function nudgeSort(delta: number) {
    setDraft((current) => ({ ...current, sort_order: current.sort_order + delta }));
  }

  return (
    <div className="space-y-5">
      <section className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Context docs</p>
        <h2 className="mt-2 text-2xl font-semibold text-slate-950">{brand.name} managed knowledge docs</h2>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
          Author brand-specific instructions as documents with typed metadata. Disabled docs stay visible here so operators can audit, restore, or replace them without losing the trail.
        </p>
      </section>

      {docsQuery.isLoading ? (
        <LoadingState label="Loading context docs…" />
      ) : docsFailed ? (
        <LoadErrorState
          title="Context docs could not be loaded"
          description="Creating and editing docs is paused until the existing docs load."
          onRetry={() => void docsQuery.refetch()}
        />
      ) : (
      <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="space-y-4">
          <section className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Collection</p>
                <h3 className="mt-2 text-lg font-semibold text-slate-900">{docs.length} docs</h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedSlug(null);
                  setEditorMode("create");
                  setDraft(emptyDraft());
                  setError(null);
                  setNotice(null);
                }}
                className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:border-slate-400"
              >
                New blank doc
              </button>
            </div>
            <div className="mt-4 space-y-3">
              {docs.map((doc) => (
                <button
                  key={doc.document_key}
                  type="button"
                  onClick={() => {
                    setSelectedSlug(doc.slug);
                    setEditorMode("edit");
                    setDraft(draftFromDoc(doc));
                    setError(null);
                    setNotice(null);
                  }}
                  className={`w-full rounded-2xl border px-4 py-3 text-left ${
                    selectedSlug === doc.slug ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-900 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-semibold">{doc.name}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      doc.enabled
                        ? selectedSlug === doc.slug
                          ? "bg-white/15 text-white"
                          : "bg-emerald-100 text-emerald-800"
                        : selectedSlug === doc.slug
                        ? "bg-white/15 text-white"
                        : "bg-amber-100 text-amber-800"
                    }`}>
                      {doc.enabled ? "Enabled" : "Archived"}
                    </span>
                  </div>
                  <p className={`mt-2 text-xs ${selectedSlug === doc.slug ? "text-slate-200" : "text-slate-500"}`}>
                    {doc.doc_type.replace(/_/g, " ")} · {doc.surface ?? "all surfaces"} · order {doc.sort_order}
                  </p>
                </button>
              ))}
            </div>
          </section>

          <section className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Starter templates</p>
            <div className="mt-4 space-y-4">
              {Object.entries(CONTEXT_DOC_TEMPLATES).map(([docType, templates]) => (
                <div key={docType}>
                  <p className="text-sm font-semibold capitalize text-slate-900">{docType.replace(/_/g, " ")}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {templates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => {
                          setSelectedSlug(null);
                          setEditorMode("create");
                          setDraft(draftFromTemplate(template));
                          setError(null);
                          setNotice(`Loaded template: ${template.label}`);
                        }}
                        className="rounded-full border border-slate-300 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-100"
                      >
                        {template.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </aside>

        <section className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Editor</p>
              <h3 className="mt-2 text-xl font-semibold text-slate-950">
                {selectedDoc ? `Editing ${selectedDoc.name}` : "Create a context doc"}
              </h3>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                Keep the document natural for human operators. Runtime structure comes from metadata like doc type, surface, and apply-when rules.
              </p>
            </div>
            {selectedDoc && selectedDoc.enabled ? (
              <button
                type="button"
                onClick={onArchive}
                disabled={saving}
                className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Archive doc
              </button>
            ) : null}
          </div>

          {notice ? (
            <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              {notice}
            </div>
          ) : null}
          {error ? (
            <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {error}
            </div>
          ) : null}

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <label className="space-y-2 text-sm">
              <span className="font-medium text-slate-800">Name</span>
              <input
                value={draft.name}
                onChange={(event) => {
                  const name = event.target.value;
                  setDraft((current) => ({
                    ...current,
                    name,
                    slug: selectedDoc ? current.slug : slugify(name),
                  }));
                }}
                className="w-full rounded-xl border border-slate-300 px-3 py-2"
                placeholder="LinkedIn surface rules"
              />
            </label>
            <label className="space-y-2 text-sm">
              <span className="font-medium text-slate-800">Slug</span>
              <input
                value={draft.slug}
                disabled={!!selectedDoc}
                onChange={(event) => setDraft((current) => ({ ...current, slug: slugify(event.target.value) }))}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 disabled:bg-slate-100 disabled:text-slate-500"
                placeholder="linkedin-surface-rules"
              />
            </label>
            <label className="space-y-2 text-sm">
              <span className="font-medium text-slate-800">Doc type</span>
              <select
                value={draft.doc_type}
                onChange={(event) => setDraft((current) => ({ ...current, doc_type: event.target.value as BrandContextDocType }))}
                className="w-full rounded-xl border border-slate-300 px-3 py-2"
              >
                <option value="persona">Persona</option>
                <option value="voice_guide">Voice guide</option>
                <option value="tone">Tone</option>
                <option value="surface_context">Surface context</option>
                <option value="custom">Custom</option>
              </select>
            </label>
            <label className="space-y-2 text-sm">
              <span className="font-medium text-slate-800">Surface</span>
              <input
                value={draft.surface}
                onChange={(event) => setDraft((current) => ({ ...current, surface: event.target.value }))}
                className="w-full rounded-xl border border-slate-300 px-3 py-2"
                placeholder="Leave blank for all surfaces"
              />
            </label>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
            <label className="space-y-2 text-sm">
              <span className="font-medium text-slate-800">Usage prompt</span>
              <textarea
                value={draft.usage_prompt}
                onChange={(event) => setDraft((current) => ({ ...current, usage_prompt: event.target.value }))}
                className="min-h-[90px] w-full rounded-xl border border-slate-300 px-3 py-2"
                placeholder="Explain when and how the agent should apply this doc."
              />
            </label>
            <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm">
              <div>
                <p className="font-medium text-slate-800">Apply when</p>
                <div className="mt-2 space-y-2">
                  {(["all", "writing", "reviewing"] as const).map((value) => (
                    <label key={value} className="flex items-center gap-2 text-slate-700">
                      <input
                        type="checkbox"
                        checked={draft.apply_when.includes(value)}
                        onChange={() => toggleApplyWhen(value)}
                      />
                      <span>{applyWhenLabel(value)}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-medium text-slate-800">Order</p>
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    aria-label="Decrease sort order"
                    onClick={() => nudgeSort(-10)}
                    className="rounded-lg border border-slate-300 px-2 py-1"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={draft.sort_order}
                    onChange={(event) => setDraft((current) => ({ ...current, sort_order: Number(event.target.value) || 0 }))}
                    className="w-24 rounded-xl border border-slate-300 px-3 py-2"
                  />
                  <button
                    type="button"
                    aria-label="Increase sort order"
                    onClick={() => nudgeSort(10)}
                    className="rounded-lg border border-slate-300 px-2 py-1"
                  >
                    +
                  </button>
                </div>
              </div>
              <label className="flex items-center gap-2 text-slate-700">
                <input
                  type="checkbox"
                  checked={draft.enabled}
                  onChange={(event) => setDraft((current) => ({ ...current, enabled: event.target.checked }))}
                />
                <span>Enabled</span>
              </label>
            </div>
          </div>

          <label className="mt-4 block space-y-2 text-sm">
            <span className="font-medium text-slate-800">Document content</span>
            <textarea
              value={draft.content}
              onChange={(event) => setDraft((current) => ({ ...current, content: event.target.value }))}
              className="min-h-[320px] w-full rounded-2xl border border-slate-300 px-4 py-3 font-mono text-sm"
              placeholder="Write the actual operator-facing guidance here."
            />
          </label>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => void onSave()}
              disabled={saving}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "Saving..." : selectedDoc ? "Save changes" : "Create doc"}
            </button>
            <button
              type="button"
              onClick={() => {
                if (selectedDoc) {
                  setDraft(draftFromDoc(selectedDoc));
                } else {
                  setDraft(emptyDraft());
                }
                setError(null);
                setNotice(null);
              }}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400"
            >
              Reset
            </button>
          </div>
        </section>
      </div>
      )}
    </div>
  );
}
